import React from 'react'
import { Button, Input, Label, Modal, Textarea } from '@/shared/ui'
import { useToast } from '@/shared/ui/Toast'
import PhoneInputIntl from '@/shared/ui/PhoneInputIntl'
import { createAffiliateLead } from '../services/affiliateLeadService'

export interface AffiliateApplicationModalProps {
  open: boolean
  onOpenChange: (v: boolean) => void
  onSubmitted?: () => void
}

export default function AffiliateApplicationModal({ open, onOpenChange, onSubmitted }: AffiliateApplicationModalProps) {
  const { add: toast } = useToast()
  const [name, setName] = React.useState('')
  const [email, setEmail] = React.useState('')
  const [whatsapp, setWhatsapp] = React.useState('')
  const [instagram, setInstagram] = React.useState('')
  const [facebook, setFacebook] = React.useState('')
  const [youtube, setYoutube] = React.useState('')
  const [discord, setDiscord] = React.useState('')
  const [otherSocials, setOtherSocials] = React.useState('')
  const [notes, setNotes] = React.useState('')
  const [submitting, setSubmitting] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  function resetForm() {
    setName('')
    setEmail('')
    setWhatsapp('')
    setInstagram('')
    setFacebook('')
    setYoutube('')
    setDiscord('')
    setOtherSocials('')
    setNotes('')
    setError(null)
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)

    const trimmedName = name.trim()
    const trimmedEmail = email.trim()
    const trimmedWhatsapp = whatsapp.trim()
    if (!trimmedName || !trimmedEmail || !trimmedWhatsapp) {
      setError('Nome, e-mail e WhatsApp são obrigatórios.')
      return
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmedEmail)) {
      setError('Informe um e-mail válido.')
      return
    }

    setSubmitting(true)
    try {
      await createAffiliateLead({
        name: trimmedName,
        email: trimmedEmail,
        whatsapp: trimmedWhatsapp,
        instagram,
        facebook,
        youtube,
        discord,
        otherSocials,
        notes,
      })
      resetForm()
      if (onSubmitted) onSubmitted()
      onOpenChange(false)
    } catch (err: any) {
      const msg = err?.message ?? 'Não foi possível enviar seu interesse. Tente novamente em instantes.'
      setError(msg)
      toast({
        variant: 'error',
        title: 'Falha ao enviar interesse',
        description: msg,
        soft: true,
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <h2 className="text-xl font-semibold">Quero ser parceiro</h2>
          <p className="text-sm text-foreground/70">
            Preencha seus dados para que o time do Cripto Alert possa avaliar seu perfil e liberar o acesso ao programa
            de afiliados.
          </p>
        </div>

        {error && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive-foreground">
            {error}
          </div>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="affiliate-name">Nome completo *</Label>
            <Input
              id="affiliate-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Como você prefere ser chamado"
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="affiliate-email">E-mail para contato *</Label>
            <Input
              id="affiliate-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="seuemail@exemplo.com"
              required
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label>WhatsApp *</Label>
          <PhoneInputIntl
            value={whatsapp}
            onChange={setWhatsapp}
            defaultCountry="BR"
            placeholderBR="(11) 98888-7777"
            required
          />
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="affiliate-instagram">Instagram</Label>
            <Input
              id="affiliate-instagram"
              value={instagram}
              onChange={(e) => setInstagram(e.target.value)}
              placeholder="@seuusuario ou link do perfil"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="affiliate-facebook">Facebook</Label>
            <Input
              id="affiliate-facebook"
              value={facebook}
              onChange={(e) => setFacebook(e.target.value)}
              placeholder="Link da página ou perfil"
            />
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="affiliate-youtube">YouTube</Label>
            <Input
              id="affiliate-youtube"
              value={youtube}
              onChange={(e) => setYoutube(e.target.value)}
              placeholder="Link do seu canal"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="affiliate-discord">Discord</Label>
            <Input
              id="affiliate-discord"
              value={discord}
              onChange={(e) => setDiscord(e.target.value)}
              placeholder="Servidor ou usuário (se relevante)"
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="affiliate-other">Outras mídias / links relevantes</Label>
          <Input
            id="affiliate-other"
            value={otherSocials}
            onChange={(e) => setOtherSocials(e.target.value)}
            placeholder="Telegram, blog, comunidade, etc."
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="affiliate-notes">Observações</Label>
          <Textarea
            id="affiliate-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Conte um pouco sobre sua audiência e como pretende divulgar o Cripto Alert."
            rows={3}
          />
        </div>

        <div className="flex items-center justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Fechar
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Enviando...' : 'Enviar interesse'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

